import type { Color } from '../../../types/colors'
import type { BadgeData } from '../utils/dom'
import { iconVars, resolveBadgeColors } from '../utils/dom'
import { createTemplateRenderer } from '../utils/fast-renderer'

/**
 * Static badge skeleton — parsed ONCE by createTemplateRenderer, then cloned
 * per cell. Mirrors the markup of `badgeHTML` (dom.ts): the icon span is hidden
 * by default and revealed in `updateBadge` when data carries an icon.
 *
 * This is the SINGLE source of truth for a grid badge's structure — `badges.ts`
 * (the array renderer) clones the same skeleton via `cloneBadgeNode` so there
 * is exactly one definition of "what a grid badge looks like".
 */
const BADGE_TEMPLATE =
  '<span class="grid-badge">' +
  '<span class="grid-badge__icon" data-component=".icon" hidden><i></i></span>' +
  '<span class="grid-badge__label"></span>' +
  '</span>'

// Parsed ONCE — cloned per badge node by `cloneBadgeNode` (used by badges.ts).
const badgeFactory = document.createElement('template')
badgeFactory.innerHTML = BADGE_TEMPLATE

const badgePrototype = badgeFactory.content.firstElementChild! as HTMLElement

/** Clone a fresh, empty badge node from the shared pre-parsed skeleton */
export const cloneBadgeNode = (): HTMLElement =>
  badgePrototype.cloneNode(true) as HTMLElement

/**
 * Apply badge data to a cloned skeleton in place — no re-parse, no re-clone.
 *
 * Execution flow:
 * 1. No label and no icon → hide the whole pill (blank cell, no empty chip).
 *    Inline `display` beats the `.grid-badge { display: inline-flex }` class,
 *    which would otherwise defeat `[hidden]`.
 * 2. Resolve colors via the shared `resolveBadgeColors` and write them to the
 *    root's inline style (same Radix tokens `badgeHTML` emits).
 * 3. Set the label `.textContent` (safe — no HTML escaping needed) and `title`.
 * 4. Toggle the icon span: hidden when no icon, otherwise set the `<i>`
 *    element's `--icon-url`/`--icon-size` vars via the shared `iconVars`
 *    contract.
 */
export const updateBadge = (root: HTMLElement, data: BadgeData): void => {
  const label = data?.label ?? ''
  const icon = data?.icon

  if (!(label || icon)) {
    root.style.display = 'none'
    return
  }

  root.style.display = ''

  const colors = resolveBadgeColors((data?.color as Color) ?? 'neutral')

  root.style.backgroundColor = colors.bg
  root.style.borderColor = colors.border
  root.style.color = colors.text
  root.title = label

  const labelEl = root.querySelector('.grid-badge__label')!
  labelEl.textContent = label

  const iconEl = root.querySelector('.grid-badge__icon')! as HTMLElement

  if (!icon) {
    iconEl.hidden = true
    return
  }

  const vars = iconVars(icon, 'xs')
  const i = iconEl.firstElementChild as HTMLElement

  i.style.setProperty('--icon-size', vars['--icon-size'])
  i.style.setProperty('--icon-url', vars['--icon-url'])
  iconEl.hidden = false
}

/** Fast badge cell renderer — clones a pre-parsed template, no innerHTML on refresh */
export const BadgeComponent = createTemplateRenderer<BadgeData>(
  BADGE_TEMPLATE,
  updateBadge
)

// Re-export type for backwards compatibility
export type { BadgeData }
